// src/CqcQuickAddContributor.js
// Small "add a contributor on the fly" popup, opened from the "+ Add
// contributor…" option at the bottom of a Worklist row's assignee dropdown
// (CqcWorklist.js) when the right person isn't in the list yet. Deliberately
// lighter than the full CqcContributors.js panel: just the fields needed to
// get someone usable right now (name, role, email, mobile) — the site isn't
// asked for, it's taken from the row that triggered this popup and shown
// read-only, since that's the whole point of opening it from a specific
// row rather than the "Manage contributors" toolbar button. On save, the
// new contributor is both created AND assigned to the row that opened this
// (see CqcWorklist.js's onCreated handler) so there's no second step.

import React, { useState } from "react";
import { cqcApi } from "./cqcApi";

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex",
             alignItems: "center", justifyContent: "center", zIndex: 1100 },
  dialog: { background: "#fff", borderRadius: 10, width: 380, maxWidth: "92vw", padding: 20,
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)", fontFamily: "system-ui, sans-serif" },
  title: { fontSize: 15, fontWeight: 700, margin: "0 0 2px" },
  sub: { fontSize: 12.5, color: "#6b7280", margin: "0 0 14px" },
  label: { fontSize: 12, fontWeight: 600, color: "#374151", margin: "10px 0 4px", display: "block" },
  input: { width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13,
           border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit" },
  row: { display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 },
  btn: { padding: "8px 14px", fontSize: 13, fontWeight: 600, border: "none", borderRadius: 6,
         background: "#2563eb", color: "#fff", cursor: "pointer" },
  btnSecondary: { padding: "8px 14px", fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: "pointer",
                  background: "#fff", color: "#374151", border: "1px solid #d1d5db" },
  err: { color: "#991b1b", fontSize: 12.5, marginTop: 8 },
};

export default function CqcQuickAddContributor({ site, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!name.trim() || !email.trim()) { setError("Name and email are required"); return; }
    setSaving(true); setError(null);
    try {
      const res = await cqcApi.saveContributor({
        siteId: site._id, name: name.trim(), role: role.trim() || null,
        email: email.trim(), mobile: mobile.trim() || null,
      });
      onCreated && onCreated(res.contributor);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={S.title}>Add contributor</div>
        <div style={S.sub}>{site.name} — they'll be assigned to this item once added</div>

        <label style={S.label}>Name</label>
        <input style={S.input} value={name} onChange={(e) => setName(e.target.value)} autoFocus />

        <label style={S.label}>Role</label>
        <input style={S.input} value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Practice Manager" />

        <label style={S.label}>Email</label>
        <input style={S.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />

        <label style={S.label}>Mobile</label>
        <input style={S.input} type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} />

        {error && <div style={S.err}>{error}</div>}

        <div style={S.row}>
          <button style={S.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={S.btn} onClick={save} disabled={saving || !name.trim() || !email.trim()}>
            {saving ? "Adding…" : "Add & assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
